import React from "react";
import { Coins, TrendingUp, ShieldCheck, Scale, Truck, FileText, CreditCard, Download, Radar, Calculator, MessageCircle, Globe, Instagram, MapPin, Users } from "lucide-react";

const IMG_GOLD = "https://images.unsplash.com/photo-1755728806819-26a994fae81a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxnb2xkJTIwYmFycyUyMGluZ290cyUyMGRhcmt8ZW58MHx8fHwxNzc2Njc2MDA0fDA&ixlib=rb-4.1.0&q=85";
const IMG_XRAY_GUN = "https://customer-assets.emergentagent.com/job_precious-exchange-6/artifacts/c1f4o4yk_Gold%20xray%20gun.jpg.avif";
const IMG_XRAY_SCREEN = "https://customer-assets.emergentagent.com/job_precious-exchange-6/artifacts/wxbtyuoq_Gold%20xray%20gun%20screen.jpeg";

export default function GoldPage({ onDownload, documents }) {
  const loiDoc = documents?.find((d) => d.id === "doc-loi");
  const ncndaDoc = documents?.find((d) => d.id === "doc-ncnda");
  const spaDoc = documents?.find((d) => d.id === "doc-spa");

  return (
    <div data-testid="view-gold" className="max-w-5xl">
      <Hero />

      <Section eyebrow="Fondamentaux" title="Pourquoi l'or reste l'actif refuge ultime" index="01">
        <p>
          L'or est le seul actif qui conserve son pouvoir d'achat depuis <strong className="text-white">plus de 5 000 ans</strong>.
          Ni banque centrale, ni gouvernement ne peut le dévaluer par décret : sa rareté est mécanique — environ
          <strong className="text-white"> 3 000 tonnes extraites par an</strong> contre une masse déjà exploitée estimée à 210 000 t.
        </p>
        <ul className="mt-6 space-y-3 text-[#A1A1AA]">
          <BulletPoint title="Protection contre l'inflation">Quand les monnaies fiduciaires se dévaluent, l'or se réévalue mécaniquement.</BulletPoint>
          <BulletPoint title="Neutralité géopolitique">Les banques centrales (Chine, Russie, Inde, Turquie) accumulent de l'or pour se désengager du dollar.</BulletPoint>
          <BulletPoint title="Liquidité mondiale 24/7">Londres, Zurich, New York, Shanghai, Dubaï, Singapour : un marché qui ne dort jamais.</BulletPoint>
          <BulletPoint title="Valeur physique universelle">Aucune contrepartie. Aucun risque de crédit. Détenu = possédé.</BulletPoint>
        </ul>
      </Section>

      <Section eyebrow="Chaîne de valeur" title="De la mine au coffre : où se crée vraiment la valeur ?" index="02">
        <p>
          L'or brut (<strong className="text-white">Dore bar</strong>) vaut beaucoup moins qu'un lingot certifié LBMA. La valeur se crée
          à chaque étape — et l'intermédiaire peut se positionner partout dans la chaîne.
        </p>

        <div className="mt-10 border border-white/10">
          {[
            { step: "01", title: "Extraction minière", detail: "Or brut à 70–92 % de pureté. Teneur variable. Marché de gré à gré très opaque.", margin: "Base" },
            { step: "02", title: "Achat aux artisans miniers (ASGM)", detail: "Afrique de l'Ouest, Amérique du Sud, Asie du Sud-Est. Prix = spot LBMA −10 à −18 %. Risque conformité OCDE.", margin: "−10 à −18 %" },
            { step: "03", title: "Dore bar", detail: "Lingot brut fondu sur site minier. 85–92 % Au. Nécessite essai (assay) et raffinage.", margin: "−5 à −8 %" },
            { step: "04", title: "Raffinage (Suisse, Dubaï, Singapour)", detail: "Métalor, PAMP, Valcambi, Argor-Heraeus, Emirates Gold. Sortie 999,9 ‰.", margin: "+2 à +3 % frais" },
            { step: "05", title: "Poinçonnage & certification LBMA", detail: "Lingot 12,5 kg (Good Delivery) ou 1 kg / 100 g. Poinçon du raffineur + numéro série. Valeur = cours LBMA plein.", margin: "Référence" },
            { step: "06", title: "Distribution finale", detail: "Banques, bourses de métaux, particuliers (bijouterie, investissement). Prime retail de 3–12 %.", margin: "+3 à +12 %" },
          ].map((row) => (
            <div key={row.step} className="grid grid-cols-12 border-b border-white/10 last:border-b-0 p-5 md:p-6 hover:bg-[#141414] transition-colors">
              <div className="col-span-2 md:col-span-1 font-serif text-xl text-[#D4AF37]">{row.step}</div>
              <div className="col-span-10 md:col-span-7">
                <div className="font-serif text-lg">{row.title}</div>
                <div className="text-sm text-[#A1A1AA] mt-2">{row.detail}</div>
              </div>
              <div className="col-span-12 md:col-span-4 md:text-right mt-3 md:mt-0">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Marge vs spot</span>
                <div className="font-mono text-white mt-1">{row.margin}</div>
              </div>
            </div>
          ))}
        </div>

        <Callout icon={ShieldCheck} title="Où l'intermédiaire gagne">
          Les deux positions les plus rentables : <strong className="text-white">(a)</strong> entre l'artisan minier et le raffineur
          (spread de 8–15 % si la logistique et la conformité OCDE sont maîtrisées), et <strong className="text-white">(b)</strong>
          entre le raffineur et un acheteur retail (Dubaï, Singapour) où la prime physique peut dépasser 5 %.
        </Callout>
      </Section>

      <Section eyebrow="Barèmes" title="LBMA vs Dubai Retail Market Price" index="03">
        <p>
          Deux cours coexistent sur le marché physique mondial, et la différence <strong className="text-white">fait ou défait la marge d'un deal</strong>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <PriceCard
            title="LBMA Gold Price"
            subtitle="Barème international"
            price="Référence mondiale"
            bullets={[
              "Fixé 2×/jour à Londres (10h30 & 15h00 GMT) par l'ICE Benchmark Administration",
              "Participants : Goldman Sachs, HSBC, JPMorgan, Standard Chartered, UBS, ICBC, etc.",
              "Cours 'spot' wholesale (1 000 oz+) — lingots 12,5 kg Good Delivery",
              "Transparent, audité, publié en USD / GBP / EUR",
            ]}
            footer="Utilisé pour les contrats interbancaires, raffineries, banques centrales."
            tone="neutral"
          />
          <PriceCard
            title="Dubai Retail Gold Price"
            subtitle="Barème local — Gold Souk / DMCC"
            price="+2 % à +5 % au-dessus LBMA"
            bullets={[
              "Publié quotidiennement par le Dubai Gold & Jewellery Group",
              "Intègre une prime physique (demande retail + coût de making)",
              "Cours 24K, 22K, 21K, 18K — affiché en AED/gramme",
              "Tire vers le haut sur les périodes de forte demande (Ramadan, mariages, Diwali)",
            ]}
            footer="Utilisé par les bijouteries, grossistes retail et investisseurs particuliers."
            tone="gold"
          />
        </div>

        <div className="mt-10 border border-white/10">
          <div className="px-6 py-4 border-b border-white/10 bg-[#141414] flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Exemple comparatif</span>
            <span className="text-xs text-[#A1A1AA]">Basé sur spot LBMA = 2 350 USD/oz (31,1 g)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] border-b border-white/10">
                  <th className="px-6 py-4">Base</th>
                  <th className="px-6 py-4">Prix / gramme</th>
                  <th className="px-6 py-4">Prix / kg</th>
                  <th className="px-6 py-4">Spread</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                <tr className="border-b border-white/10">
                  <td className="px-6 py-4">LBMA spot</td><td className="px-6 py-4 text-white">75,56 USD</td><td className="px-6 py-4 text-white">75 560 USD</td><td className="px-6 py-4 text-[#A1A1AA]">—</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="px-6 py-4">Dubai retail (24K)</td><td className="px-6 py-4 text-white">77,45 USD</td><td className="px-6 py-4 text-white">77 450 USD</td><td className="px-6 py-4 text-[#D4AF37]">+2,5 %</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Marge brute sur 10 kg</td><td className="px-6 py-4 text-white" colSpan={2}>≈ 18 900 USD avant frais</td><td className="px-6 py-4 text-[#34C759]">Positif</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <Callout icon={Scale} title="Stratégie intermédiaire">
          Acheter coté LBMA (raffinerie ou banque), revendre coté Dubaï retail. Le spread de 2–5 % absorbe la logistique
          sécurisée (+0,3 %), l'assurance (+0,1 %) et la commission IMFPA (1–2 %). Reste net : 1–2,5 %.
        </Callout>

        <Callout icon={ShieldCheck} title="Astuce terrain : la mention « product age : below 5y »">
          Sur la plupart des SCO sérieux, vous verrez la mention <em className="text-white">« product age : below 5 years »</em>.
          Ce n'est pas un détail cosmétique : au-delà de 5 ans, un lingot doit être <strong className="text-white">refondu
          et re-poinçonné</strong> avec de nouveaux numéros de série par une raffinerie LBMA. Coût : 50–120 $ par kg +
          délai logistique. Un deal qui ignore cette règle est soit fait par un amateur, soit volontairement flou pour
          masquer l'origine réelle du métal. Exigez toujours la date de coulage (<em>date of cast</em>) sur l'assay
          report.
        </Callout>
      </Section>

      <Section eyebrow="Calcul du prix" title="Pricing 18, 22, 24 carats : la formule" index="04">
        <p>
          Les bijoux et lingots ne sont pas tous en 24 carats (pur à 99,9 %). Pour valoriser ou négocier un lot mixte,
          vous avez besoin d'<strong className="text-white">une seule formule</strong> — celle que tout broker aguerri
          connaît par cœur.
        </p>

        <div className="mt-10 border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c] p-8 md:p-10">
          <div className="flex items-center gap-3">
            <Calculator className="h-5 w-5 text-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Formule universelle</span>
          </div>
          <div className="mt-6 font-serif text-2xl md:text-3xl text-white leading-relaxed">
            Prix(carats) = <span className="text-[#D4AF37]">(carats / 24)</span> × Prix(24K)
          </div>
          <p className="mt-5 text-sm text-[#A1A1AA]">
            Vous divisez la pureté actuelle par la pureté maximale (24), puis vous multipliez par le prix du 24 carats.
            Simple, mais souvent mal appliquée par les amateurs.
          </p>
        </div>

        <div className="mt-10 border border-white/10">
          <div className="px-6 py-4 bg-[#141414] border-b border-white/10 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Exemple concret</span>
            <span className="text-xs text-[#A1A1AA]">Base : 24K = 60 000 USD / kg</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] border-b border-white/10">
                <th className="px-6 py-4">Pureté</th>
                <th className="px-6 py-4">Ratio (carats / 24)</th>
                <th className="px-6 py-4">Calcul</th>
                <th className="px-6 py-4">Prix / kg</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-b border-white/10">
                <td className="px-6 py-4 text-white">24K (pur 99,9%)</td>
                <td className="px-6 py-4 text-[#A1A1AA]">24 / 24 = 1,000</td>
                <td className="px-6 py-4 text-[#A1A1AA]">1,000 × 60 000</td>
                <td className="px-6 py-4 text-[#D4AF37]">60 000 USD</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="px-6 py-4 text-white">22K (bijouterie orientale)</td>
                <td className="px-6 py-4 text-[#A1A1AA]">22 / 24 = 0,917</td>
                <td className="px-6 py-4 text-[#A1A1AA]">0,917 × 60 000</td>
                <td className="px-6 py-4 text-[#D4AF37]">55 000 USD</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="px-6 py-4 text-white">21K (Moyen-Orient)</td>
                <td className="px-6 py-4 text-[#A1A1AA]">21 / 24 = 0,875</td>
                <td className="px-6 py-4 text-[#A1A1AA]">0,875 × 60 000</td>
                <td className="px-6 py-4 text-[#D4AF37]">52 500 USD</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="px-6 py-4 text-white">18K (bijouterie européenne)</td>
                <td className="px-6 py-4 text-[#A1A1AA]">18 / 24 = 0,750</td>
                <td className="px-6 py-4 text-[#A1A1AA]">0,750 × 60 000</td>
                <td className="px-6 py-4 text-[#D4AF37]">45 000 USD</td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-white">14K (USA, industrie)</td>
                <td className="px-6 py-4 text-[#A1A1AA]">14 / 24 = 0,583</td>
                <td className="px-6 py-4 text-[#A1A1AA]">0,583 × 60 000</td>
                <td className="px-6 py-4 text-[#D4AF37]">35 000 USD</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Callout icon={Scale} title="Attention au prix 'par carat' retail">
          Dans les pays du Golfe, les bijoutiers affichent souvent un prix AED/gramme pour du 22K ou 21K, pas pour
          du 24K. Ne comparez jamais sans ramener au 24K. Formule inverse : pour remonter au cours 24K à partir d'un
          prix 22K, divisez par 0,917.
        </Callout>
      </Section>

      <Section eyebrow="Vérification" title="XRF Gun : mesurer la pureté en 3 secondes" index="05">
        <p>
          Un vendeur malhonnête peut <strong className="text-white">trafiquer un rapport de raffinage</strong> et
          vous présenter un lingot estampillé 24K alors qu'il est en réalité 22K (ou pire, plaqué). Le seul moyen
          fiable et instantané de vérifier sur place : un <strong className="text-white">pistolet XRF</strong> (X-Ray
          Fluorescence) — portable, précis au dixième, acheté en ligne pour 8 000 à 25 000 USD.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="border border-white/10 bg-[#0c0c0c] overflow-hidden">
            <div className="aspect-[4/3] bg-[#141414]">
              <img
                src={IMG_XRAY_GUN}
                alt="Gold XRF analyzer gun"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Le pistolet</div>
              <div className="font-serif text-xl mt-2">XRF Analyzer portatif</div>
              <p className="mt-3 text-sm text-[#A1A1AA]">
                Pointez le canon contre le lingot, appuyez sur la détente, attendez 3 secondes. Le pistolet émet un
                rayon X qui excite les atomes en surface (pénétration ~100 µm). Chaque métal renvoie une signature
                énergétique unique — or, argent, cuivre, nickel, iridium.
              </p>
            </div>
          </div>
          <div className="border border-white/10 bg-[#0c0c0c] overflow-hidden">
            <div className="aspect-[4/3] bg-[#141414]">
              <img
                src={IMG_XRAY_SCREEN}
                alt="XRF analyzer screen showing gold purity"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">L'écran</div>
              <div className="font-serif text-xl mt-2">Résultat instantané</div>
              <p className="mt-3 text-sm text-[#A1A1AA]">
                Le pourcentage exact de chaque élément s'affiche. Un vrai lingot 999,9 ‰ LBMA doit afficher
                <span className="text-white font-mono"> Au ≥ 99,95 %</span>. Moins de 99 % = pas du 24K. Moins de 95 % = fraude manifeste.
              </p>
            </div>
          </div>
        </div>

        <Callout icon={Radar} title="Protocole de vérification terrain">
          <ol className="list-decimal pl-5 space-y-2 mt-2">
            <li>Mesurer en <strong className="text-white">3 points différents</strong> du lingot (faces et tranche) — un plaqué or révèle une variation entre surface et profondeur.</li>
            <li>Comparer à l'<strong className="text-white">assay report officiel</strong> fourni par le vendeur (Metalor, PAMP, Valcambi, Argor-Heraeus).</li>
            <li>Vérifier aussi <strong className="text-white">le poids</strong> sur balance certifiée (1 kg Good Delivery = 1 000,00 g ± 0,05 g).</li>
            <li>Écart &gt;0,5 % entre XRF et assay = refus immédiat, ou renégociation du prix selon pureté réelle via la formule (carats / 24) × Prix(24K).</li>
          </ol>
        </Callout>

        <p className="mt-8 text-sm text-[#A1A1AA]">
          <strong className="text-white">Bon à savoir :</strong> les pistolets XRF grand public (Bruker Tracer, Olympus
          Vanta, Thermo Niton) se revendent facilement sur le marché de l'occasion. L'investissement est amorti dès le
          premier deal évité. Considérez-le comme <em className="text-[#D4AF37]">votre assurance anti-arnaque n°1</em>.
        </p>
      </Section>

      <Section eyebrow="Procédures" title="FOB, CIF, EXW : les Incoterms de l'or" index="06">
        <p>
          Les Incoterms 2020 définissent <strong className="text-white">qui paie quoi, qui assure quoi, et où le risque bascule</strong>.
          Les quatre les plus utilisés sur l'or physique :
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <IncotermCard code="EXW" name="Ex Works" desc="L'acheteur enlève à la raffinerie. Risque + logistique = acheteur. Moins cher, plus risqué." />
          <IncotermCard code="FOB" name="Free On Board" desc="Le vendeur livre à l'aéroport de départ (ex : Zurich ZRH). À partir de là, l'acheteur paie le fret et l'assurance." />
          <IncotermCard code="CIF" name="Cost, Insurance, Freight" desc="Le vendeur paie le fret sécurisé (Brink's, Loomis) et l'assurance jusqu'au port/aéroport de destination. Le plus utilisé." />
          <IncotermCard code="DAP" name="Delivered At Place" desc="Le vendeur livre au coffre de l'acheteur. Tout inclus. Le plus confortable — le plus cher." />
        </div>
      </Section>

      <Section eyebrow="Paiement" title="MT103, MT760, SBLC, DLC : les instruments bancaires" index="07">
        <p>
          L'or est rarement payé en cash : les montants (500 k$ à 100 M$) passent par le réseau SWIFT via des messages codés.
          <strong className="text-white"> Connaître ces codes, c'est savoir si un deal est sérieux ou bidon.</strong>
        </p>
        <div className="mt-10 space-y-3">
          {[
            { code: "MT103", label: "Virement client simple (paiement final)", detail: "Le plus basique. Ordre de paiement SWIFT de banque à banque pour le compte d'un client. Utilisé pour payer le solde après livraison.", risk: "Faible si contre-documents conformes" },
            { code: "MT103/23", label: "MT103 avec condition", detail: "Virement conditionnel — débloqué contre preuve de livraison ou documents spécifiques. Quasi-SBLC en version allégée.", risk: "Moyen — clauses à auditer" },
            { code: "MT760", label: "SBLC / DLC — Garantie bancaire SWIFT", detail: "Standby Letter of Credit. La banque de l'acheteur garantit le paiement au vendeur si conditions documentaires remplies. Le gold standard des deals >1 M$.", risk: "Très faible — instrument irrévocable" },
            { code: "MT700", label: "Documentary Letter of Credit (DLC)", detail: "Crédit documentaire classique. Le vendeur est payé à présentation des documents (Bill of Lading, Assay Report, Certificate of Origin).", risk: "Faible — procédure cadrée ICC UCP 600" },
            { code: "MT799", label: "Pre-advice / Readiness to transact", detail: "Message SWIFT de bonne foi (non-financier). L'acheteur confirme qu'il a les fonds. Pré-requis avant MT760.", risk: "Aucun — juste un signal" },
            { code: "MT999", label: "Message libre", detail: "Correspondance SWIFT libre entre banques. Souvent utilisé pour clarifier des termes avant émission d'un MT760.", risk: "Aucun — communication" },
          ].map((p) => (
            <div key={p.code} className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-white/25 transition-colors">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-[#D4AF37] text-lg">{p.code}</span>
                  <span className="font-serif text-lg text-white">{p.label}</span>
                </div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA]">Risque : {p.risk}</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mt-3">{p.detail}</p>
            </div>
          ))}
        </div>

        <Callout icon={CreditCard} title="Séquence type d'un deal or sécurisé">
          <ol className="list-decimal pl-5 space-y-2 mt-2">
            <li>NCNDA signé entre toutes les parties (vous inclus, pour verrouiller votre commission via IMFPA).</li>
            <li>LOI de l'acheteur → réponse du vendeur (SCO ou Full Corporate Offer).</li>
            <li>Procédures documentaires : Assay Report, Certificate of Origin (Kimberley pour diamants ; OCDE pour or), Export License.</li>
            <li>Acheteur émet MT799 (readiness) → Vendeur émet POP (Proof of Product : assay, SKR, dip test).</li>
            <li>Acheteur émet MT760 (SBLC) en faveur du vendeur → Vendeur expédie.</li>
            <li>Livraison au coffre désigné → inspection par assayer indépendant.</li>
            <li>Paiement final MT103 → commission IMFPA versée en parallèle.</li>
          </ol>
        </Callout>
      </Section>

      <Section eyebrow="Ressources" title="Documents à télécharger pour un deal or" index="08">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DocRef doc={loiDoc} onDownload={onDownload} accent="LOI — Exemple réel Or" note="Thanatos LOI — Letter of Intent d'un acheteur professionnel. À adapter à votre deal." />
          <DocRef doc={spaDoc} onDownload={onDownload} accent="SPA — Sales & Purchase Agreement" note="Marena Gold MGM — Contrat ferme avec SBLC DXB. La structure complète d'un deal bouclé." />
          <DocRef doc={ncndaDoc} onDownload={onDownload} accent="NCNDA — Template ICC" note="Indispensable avant tout échange d'informations sensibles. Protège votre commission." />
        </div>
        <p className="mt-6 text-xs text-[#52525B]">
          L'exemple de SCO spécifique à l'or sera ajouté prochainement.
        </p>
      </Section>

      <Section eyebrow="Rolodex" title="Fournisseurs & partenaires — Or" index="09">
        <p>
          Les contacts directs qui composent un réseau opérationnel dans l'or :
          <strong className="text-white"> producteurs, traders, laboratoires et équipementiers</strong> triés sur le volet.
          WhatsApp cliquable pour contact instantané.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          <SupplierCard
            name="Gold Era"
            tag="Trader · Dubaï"
            location="Dubai, UAE"
            whatsapps={["+971 56 993 6797"]}
            website="https://www.dubai.gold-era.com"
          />
          <SupplierCard
            name="Gold Metal DMCC"
            tag="Trader DMCC · Dubaï"
            location="Dubai, UAE"
            whatsapps={["+971 502 298 202"]}
            website="https://www.goldmetaldmcc.com"
            instagram="goldmetaluae"
          />
          <SupplierCard
            name="Marena Gold"
            tag="Raffineur · Mali — Sibi Abubacar"
            location="Mali"
            whatsapps={["+223 77 78 06 77"]}
            website="https://www.marenagoldmali.com"
          />
          <SupplierCard
            name="Pulpar Ambil"
            tag="Trader · Dubaï"
            location="Dubai, UAE"
            whatsapps={["+971 56 218 0292", "+971 56 218 0291"]}
          />
          <SupplierCard
            name="Goldbox · Pureray"
            tag="Équipement — Gold testing machine"
            location="Shenzhen, China"
            whatsapps={["+852 5565 5728"]}
            website="https://www.pureray.com"
          />
        </div>
      </Section>
    </div>
  );
}

// ---------- Subcomponents ----------
const Hero = () => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <img src={IMG_GOLD} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Coins className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Secteur · 01</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        L'<em className="italic text-[#F5F5DC]">Or</em> — du Dore bar <br className="hidden md:block" /> au lingot <span className="text-[#D4AF37]">LBMA</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">
        Comprendre la supply chain, les deux cours de référence (LBMA vs Dubaï), les Incoterms et les instruments
        bancaires SWIFT — pour cadrer un deal gagnant.
      </p>
    </div>
  </section>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-20">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light mt-2 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const BulletPoint = ({ title, children }) => (
  <li className="flex gap-3">
    <span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" />
    <div>
      <span className="text-white">{title}.</span> <span>{children}</span>
    </div>
  </li>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const PriceCard = ({ title, subtitle, price, bullets, footer, tone }) => (
  <div className={`border ${tone === "gold" ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c]" : "border-white/10 bg-[#0c0c0c]"} p-7`}>
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">{subtitle}</div>
    <div className="font-serif text-2xl text-white mt-2">{title}</div>
    <div className={`mt-6 font-mono text-lg ${tone === "gold" ? "text-[#D4AF37]" : "text-white"}`}>{price}</div>
    <ul className="mt-6 space-y-2 text-sm text-[#A1A1AA]">
      {bullets.map((b, i) => <li key={i} className="flex gap-2"><span className="text-[#D4AF37]">·</span><span>{b}</span></li>)}
    </ul>
    <p className="mt-6 pt-5 border-t border-white/10 text-xs text-[#52525B]">{footer}</p>
  </div>
);

const IncotermCard = ({ code, name, desc }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-[#D4AF37]/30 transition-colors">
    <div className="flex items-baseline gap-4">
      <Truck className="h-4 w-4 text-[#D4AF37]" />
      <span className="font-mono text-[#D4AF37] text-lg">{code}</span>
      <span className="font-serif text-base text-white">{name}</span>
    </div>
    <p className="text-sm text-[#A1A1AA] mt-3">{desc}</p>
  </div>
);

const DocRef = ({ doc, onDownload, accent, note }) => {
  if (!doc) return null;
  return (
    <div className="border border-white/10 bg-[#0c0c0c] p-6 group hover:border-[#D4AF37]/30 transition-colors">
      <div className="flex items-center gap-3">
        <FileText className="h-4 w-4 text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{accent}</span>
      </div>
      <div className="font-serif text-xl mt-3">{doc.name}</div>
      <p className="text-sm text-[#A1A1AA] mt-3">{note}</p>
      <button
        onClick={() => onDownload(doc)}
        data-testid={`gold-download-${doc.id}`}
        className="mt-5 inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
      >
        <Download className="h-3.5 w-3.5" /> Télécharger PDF
      </button>
    </div>
  );
};


// Reusable supplier/contact card — WhatsApp clickable, optional website + IG
const SupplierCard = ({ name, tag, location, whatsapps = [], website, instagram, phone }) => (
  <div data-testid={`supplier-card-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border border-white/10 bg-[#0c0c0c] p-6 md:p-7 hover:border-[#D4AF37]/50 transition-colors flex flex-col">
    <div className="flex items-center gap-3">
      <Users className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{tag}</span>
    </div>
    <h3 className="font-serif text-xl mt-3 text-white">{name}</h3>
    {location && (
      <div className="mt-2 flex items-center gap-2 text-xs text-[#A1A1AA]">
        <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
        <span>{location}</span>
      </div>
    )}
    <div className="mt-5 space-y-2.5 text-sm">
      {whatsapps.map((w) => (
        <a
          key={w}
          href={`https://wa.me/${w.replace(/\D/g, "")}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#25D366] transition-colors group"
        >
          <MessageCircle className="h-4 w-4 shrink-0 text-[#25D366] group-hover:scale-110 transition-transform" />
          <span className="font-mono tracking-tight">{w}</span>
        </a>
      ))}
      {phone && (
        <a
          href={`tel:${phone.replace(/\s/g, "")}`}
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
        >
          <CreditCard className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="font-mono tracking-tight">{phone}</span>
        </a>
      )}
      {website && (
        <a
          href={website}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors break-all"
        >
          <Globe className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="truncate">{website.replace(/^https?:\/\//, "")}</span>
        </a>
      )}
      {instagram && (
        <a
          href={`https://instagram.com/${instagram.replace(/^@/, "")}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#E1306C] transition-colors"
        >
          <Instagram className="h-4 w-4 shrink-0 text-[#E1306C]" />
          <span>@{instagram.replace(/^@/, "")}</span>
        </a>
      )}
    </div>
  </div>
);
